/*
 * PX — Tiroir panier (remplace le cart-drawer.js de Dawn).
 *
 * Principes :
 *  - Un seul Web Component <cart-drawer>, zéro dépendance, délégation d'événements
 *    (les listeners survivent aux re-rendus de #PxDrawerItems).
 *  - calculerPort() est LA fonction pure du port et de la jauge — aucune autre
 *    copie de la grille dans le code. La config vient des data-* posés par Liquid.
 *  - Mises à jour optimistes : l'UI réagit tout de suite, l'API confirme ;
 *    rollback visuel si elle échoue. AbortController par ligne contre les courses.
 *  - Suppression = toast « Annuler » 5 s, l'appel API ne part qu'à l'expiration.
 *  - Tous les textes viennent de #PxDrawerStrings (Liquid), rien en dur ici.
 */
(function () {
  'use strict';

  /**
   * Fonction pure : port + état de jauge pour un sous-total donné (centimes).
   * cfg = { palier, seuil, bas, haut } en centimes.
   * Retourne { zone: 'info'|'jauge'|'gratuit', port, progression [0..1], manque }.
   */
  function calculerPort(sousTotal, cfg) {
    if (sousTotal >= cfg.seuil) {
      return { zone: 'gratuit', port: 0, progression: 1, manque: 0 };
    }
    if (sousTotal >= cfg.palier) {
      return {
        zone: 'jauge',
        port: cfg.haut,
        progression: (sousTotal - cfg.palier) / (cfg.seuil - cfg.palier),
        manque: cfg.seuil - sousTotal,
      };
    }
    return { zone: 'info', port: cfg.bas, progression: 0, manque: cfg.seuil - sousTotal };
  }

  /* 1 239,00 € à la française, à partir de centimes. */
  function eur(cents) {
    var v = (cents / 100).toFixed(2).replace('.', ',');
    var p = v.split(',');
    p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return p.join(',') + ' €';
  }

  /* Variante sans decimales inutiles : 100,00 EUR -> 100 EUR (messages de jauge). */
  function eurC(cents) { return eur(cents).replace(',00 €', ' €').replace(',00 €', ' €'); }

  function debounce(fn, ms) {
    var t;
    return function () {
      var a = arguments, c = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(c, a); }, ms);
    };
  }

  /* Prochain jour ouvré (samedi/dimanche exclus). */
  function jourOuvreSuivant(d) {
    var n = new Date(d);
    do { n.setDate(n.getDate() + 1); } while (n.getDay() === 0 || n.getDay() === 6);
    return n;
  }

  /* Fenêtre de livraison estimée : expédition aujourd'hui si avant 14 h un jour
     ouvré, sinon prochain jour ouvré ; réception J+1 à J+3 ouvrés après expédition. */
  function fenetreLivraison() {
    var now = new Date();
    var exp = new Date(now);
    var ouvre = exp.getDay() !== 0 && exp.getDay() !== 6;
    if (!ouvre || now.getHours() >= 14) exp = jourOuvreSuivant(exp);
    var debut = jourOuvreSuivant(exp);
    var fin = jourOuvreSuivant(jourOuvreSuivant(debut));
    var fmt = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' });
    return { debut: fmt.format(debut), fin: fmt.format(fin) };
  }

  var DELAI_DEBOUNCE = 300;
  var DELAI_UNDO = 5000;

  class PxCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      this.strings = JSON.parse((this.querySelector('#PxDrawerStrings') || { textContent: '{}' }).textContent);
      this.aborts = {};          // AbortController par clé de ligne (item.key)
      this.pendingRemove = {};   // suppressions en attente d'annulation, par item.key
      this.dernierFocus = null;

      this._bind();
      this._refreshFromRoot();
      this._interceptIconePanier();
    }

    /* ---------- lecture d'état ---------- */

    _cfg() {
      return {
        palier: parseInt(this.dataset.palier, 10) || 6000,
        seuil: parseInt(this.dataset.seuil, 10) || 10000,
        bas: parseInt(this.dataset.bas, 10) || 190,
        haut: parseInt(this.dataset.haut, 10) || 590,
      };
    }
    _total() { return parseInt(this.dataset.total, 10) || 0; }

    /* ---------- ouverture / fermeture ---------- */

    open() {
      if (this.classList.contains('is-open')) return;
      this.dernierFocus = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('pxd-lock');
      var panel = this.querySelector('.pxd-panel');
      if (panel) panel.focus({ preventScroll: true });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('pxd-lock');
      if (this.dernierFocus && this.dernierFocus.focus) this.dernierFocus.focus();
    }

    /* ---------- rendu ---------- */

    /* Re-rend la zone articles + relit les data-* depuis le HTML de section fourni.
       Appelé en interne après mutation, et par la fiche produit après un ajout. */
    renderSection(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var src = doc.querySelector('cart-drawer');
      if (!src) return;
      var items = src.querySelector('#PxDrawerItems');
      var mine = this.querySelector('#PxDrawerItems');
      if (items && mine) mine.innerHTML = items.innerHTML;
      var ship = src.querySelector('#PxShip');
      var myShip = this.querySelector('#PxShip');
      if (ship && myShip) myShip.hidden = ship.hidden;
      this.dataset.count = src.dataset.count || '0';
      this.dataset.total = src.dataset.total || '0';
      this.dataset.savings = src.dataset.savings || '0';
      this.classList.toggle('is-empty', this.dataset.count === '0');
      var foot = this.querySelector('.pxd-foot');
      if (foot) foot.hidden = this.dataset.count === '0';
      this._refreshFromRoot();
    }

    /* Recalcule tout l'affichage dérivé (jauge, totaux, badge, ETA, upsell)
       à partir des data-* de la racine. */
    _refreshFromRoot() {
      var total = this._total();
      var cfg = this._cfg();
      var etat = calculerPort(total, cfg);
      this._appliquerJauge(etat, total > 0);
      this._appliquerTotaux(total, etat);
      this._majBadge(parseInt(this.dataset.count, 10) || 0);
      this._majEta();
      this._choisirUpsell(etat);
    }

    _appliquerJauge(etat, visible) {
      var ship = this.querySelector('#PxShip');
      if (!ship) return;
      ship.hidden = !visible;
      var precedent = ship.dataset.etat;
      ship.dataset.etat = etat.zone;

      if (etat.zone === 'jauge') {
        var msg = this.querySelector('#PxShipMsg');
        if (msg) msg.innerHTML = this.strings.manque.replace('[montant]', '<b>' + eur(etat.manque) + '</b>');
        var fill = this.querySelector('#PxShipFill');
        if (fill) fill.style.transform = 'scaleX(' + etat.progression.toFixed(4) + ')';
        var track = this.querySelector('.pxd-ship__track');
        if (track) track.setAttribute('aria-valuenow', Math.round(etat.progression * 100));
      } else if (etat.zone === 'info') {
        var info = this.querySelector('#PxShipInfo');
        if (info) {
          info.textContent = this.strings.infoLigne
            .replace('[bas]', eurC(this._cfg().bas))
            .replace('[seuil]', eurC(this._cfg().seuil));
        }
      }

      /* Toast au franchissement du seuil — une seule fois par session,
         jamais re-déclenché par un aller-retour sous le seuil. */
      if (etat.zone === 'gratuit' && precedent && precedent !== 'gratuit' && !sessionStorage.getItem('pxd_toast_gratuit')) {
        sessionStorage.setItem('pxd_toast_gratuit', '1');
        this._toast(this.strings.toastGratuit);
      }
    }

    _appliquerTotaux(total, etat) {
      var sub = this.querySelector('#PxDrawerSubtotal');
      if (sub) sub.textContent = eur(total);
      var ship = this.querySelector('#PxDrawerShip');
      if (ship) {
        ship.textContent = etat.port === 0 ? this.strings.portOffert : eur(etat.port);
        ship.classList.toggle('is-free', etat.port === 0);
      }
      var tot = this.querySelector('#PxDrawerTotal');
      if (tot) tot.textContent = eur(total + etat.port);
      var cta = this.querySelector('#PxDrawerCtaTotal');
      if (cta) cta.textContent = eur(total + etat.port);
      var eco = parseInt(this.dataset.savings, 10) || 0;
      var ecoRow = this.querySelector('#PxDrawerEcoRow');
      if (ecoRow) {
        ecoRow.hidden = eco <= 0;
        var ecoEl = this.querySelector('#PxDrawerEco');
        if (ecoEl) ecoEl.textContent = eur(eco);
      }
    }

    _majBadge(count) {
      var b = document.getElementById('ps-nav-count');
      if (b) {
        b.textContent = count;
        b.style.display = count > 0 ? '' : 'none';
      }
      var c = this.querySelector('#PxDrawerCount');
      if (c) c.textContent = count;
    }

    _majEta() {
      var el = this.querySelector('#PxDrawerEta');
      if (!el) return;
      if ((parseInt(this.dataset.count, 10) || 0) === 0) { el.textContent = ''; return; }
      var f = fenetreLivraison();
      el.textContent = this.strings.eta.replace('[debut]', f.debut).replace('[fin]', f.fin);
    }

    /* Upsell : 3 cartes max. En zone jauge, priorité aux prix qui rapprochent
       le plus du seuil (écart minimal avec le manque). */
    _choisirUpsell(etat) {
      var cards = Array.prototype.slice.call(this.querySelectorAll('.pxd-up__card'));
      if (!cards.length) return;
      var tri = cards.slice();
      if (etat.zone === 'jauge') {
        tri.sort(function (a, b) {
          var pa = parseInt(a.dataset.price, 10) || 0;
          var pb = parseInt(b.dataset.price, 10) || 0;
          return Math.abs(pa - etat.manque) - Math.abs(pb - etat.manque);
        });
      }
      cards.forEach(function (c) { c.hidden = true; });
      tri.slice(0, 3).forEach(function (c) { c.hidden = false; });
    }

    /* ---------- mutations panier ---------- */

    /* Change la quantité d'une ligne. Optimiste : totaux recalculés localement
       avant la réponse ; reconciliation (ou rollback) au retour de l'API. */
    _changerQuantite(key, quantite) {
      var ligne = this.querySelector('.pxd-line[data-key="' + key + '"]');
      if (!ligne) return;
      var prix = parseInt(ligne.dataset.price, 10) || 0;
      var avant = parseInt(ligne.dataset.qty, 10) || 0;
      if (quantite === avant) return;

      /* Optimisme : quantité, prix de ligne et totaux bougent tout de suite. */
      var input = ligne.querySelector('[data-pxd-qty]');
      if (input) input.value = quantite;
      var lp = ligne.querySelector('[data-pxd-lineprice]');
      if (lp) lp.textContent = eur(prix * quantite);
      var totalAvant = this._total();
      this.dataset.total = String(totalAvant + prix * (quantite - avant));
      ligne.dataset.qty = String(quantite);
      if (quantite === 0) ligne.classList.add('is-removing');
      this._refreshFromRoot();

      /* Une requête par ligne : la précédente est annulée (anti-course). */
      if (this.aborts[key]) this.aborts[key].abort();
      var ctrl = new AbortController();
      this.aborts[key] = ctrl;
      ligne.classList.add('is-busy');

      var self = this;
      fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        signal: ctrl.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantite, sections: 'cart-drawer' }),
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.errors || res.status) throw new Error(res.errors || res.description);
          /* La quantité servie peut différer (stock) : la section fait foi. */
          if (res.sections && res.sections['cart-drawer']) self.renderSection(res.sections['cart-drawer']);
          var it = (res.items || []).filter(function (i) { return i.key === key; })[0];
          if (quantite > 0 && it && it.quantity < quantite) self._toast(self.strings.stockMax);
        })
        .catch(function (e) {
          if (e.name === 'AbortError') return;
          /* Rollback visuel : on re-rend depuis le serveur (état vrai). */
          self._resync();
          self._toast(self.strings.erreur, true);
        })
        .finally(function () {
          ligne.classList.remove('is-busy');
        });
    }

    /* Re-rendu complet depuis le serveur (rollback ou resynchronisation). */
    _resync() {
      var self = this;
      fetch(window.routes.cart_url + '?section_id=cart-drawer')
        .then(function (r) { return r.text(); })
        .then(function (html) { self.renderSection(html); })
        .catch(function () {});
    }

    /* Suppression douce : la ligne se replie, un toast « Annuler » reste 5 s,
       l'appel API ne part qu'à l'expiration. Jamais de confirm() bloquant. */
    _retirer(key) {
      var ligne = this.querySelector('.pxd-line[data-key="' + key + '"]');
      if (!ligne || this.pendingRemove[key]) return;
      ligne.classList.add('is-removing');
      var self = this;
      var titre = ligne.dataset.title || '';
      var timer = setTimeout(function () {
        delete self.pendingRemove[key];
        self._changerQuantite(key, 0);
      }, DELAI_UNDO);
      this.pendingRemove[key] = { timer: timer };
      this._toast(this.strings.retire.replace('[titre]', titre), false, {
        label: this.strings.annuler,
        action: function () {
          clearTimeout(timer);
          delete self.pendingRemove[key];
          ligne.classList.remove('is-removing');
        },
      });
    }

    /* Ajout (upsell) en 1 clic sans quitter le tiroir. */
    _ajouter(variantId, btn) {
      var self = this;
      if (btn) btn.disabled = true;
      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: 'cart-drawer' }),
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) throw new Error(res.description);
          if (res.sections && res.sections['cart-drawer']) self.renderSection(res.sections['cart-drawer']);
        })
        .catch(function () { self._toast(self.strings.erreur, true); })
        .finally(function () { if (btn) btn.disabled = false; });
    }

    /* ---------- toasts ---------- */

    _toast(texte, estErreur, undo) {
      var zone = this.querySelector('.pxd-toasts');
      if (!zone) return;
      var t = document.createElement('div');
      t.className = 'pxd-toast' + (estErreur ? ' is-err' : '');
      t.setAttribute('role', 'status');
      var span = document.createElement('span');
      span.textContent = texte;
      t.appendChild(span);
      if (undo) {
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = undo.label;
        b.addEventListener('click', function () { undo.action(); t.remove(); });
        t.appendChild(b);
      }
      zone.appendChild(t);
      setTimeout(function () { t.classList.add('is-out'); }, DELAI_UNDO - 300);
      setTimeout(function () { t.remove(); }, DELAI_UNDO);
    }

    /* ---------- câblage ---------- */

    _bind() {
      var self = this;

      /* Délégation : tout survit aux re-rendus de la zone articles. */
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-pxd-close]')) { self.close(); return; }
        var plus = e.target.closest('[data-pxd-plus]');
        var moins = e.target.closest('[data-pxd-minus]');
        if (plus || moins) {
          var ligne = e.target.closest('.pxd-line');
          if (!ligne) return;
          var q = parseInt(ligne.dataset.qty, 10) || 0;
          var key = ligne.dataset.key;
          if (moins && q <= 1) { self._retirer(key); return; }
          self._changerQuantite(key, q + (plus ? 1 : -1));
          return;
        }
        var del = e.target.closest('[data-pxd-remove]');
        if (del) {
          var l2 = e.target.closest('.pxd-line');
          if (l2) self._retirer(l2.dataset.key);
          return;
        }
        var add = e.target.closest('[data-pxd-add]');
        if (add) { self._ajouter(parseInt(add.getAttribute('data-pxd-add'), 10), add); return; }
        var promo = e.target.closest('[data-pxd-promo]');
        if (promo) { self._appliquerPromo(); }
      });

      /* Saisie directe dans le champ quantité — debounce 300 ms. */
      this.addEventListener('input', debounce(function (e) {
        var input = e.target.closest('[data-pxd-qty]');
        if (!input) return;
        var ligne = input.closest('.pxd-line');
        var v = parseInt(input.value, 10);
        if (isNaN(v) || v < 0) return;
        var key = ligne.dataset.key;
        if (v === 0) { self._retirer(key); } else { self._changerQuantite(key, v); }
      }, DELAI_DEBOUNCE));

      /* Échap ferme ; piège à focus dans le panneau. */
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { self.close(); return; }
        if (e.key !== 'Tab' || !self.classList.contains('is-open')) return;
        var focusables = self.querySelectorAll('button:not([disabled]), a[href], input, summary, [tabindex="-1"].pxd-panel');
        if (!focusables.length) return;
        var premier = focusables[0], dernier = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
        else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
      });

      /* Anti double-clic sur le CTA : spinner + désactivation, la soumission part. */
      var form = this.querySelector('.pxd-payform');
      if (form) {
        form.addEventListener('submit', function () {
          var cta = self.querySelector('#PxDrawerCta');
          if (cta) {
            cta.classList.add('is-loading');
            var s = cta.querySelector('.pxd-cta__spin');
            if (s) s.hidden = false;
            setTimeout(function () { cta.disabled = true; }, 0);
          }
        });
      }

      /* Swipe-vers-la-gauche pour supprimer, sur mobile. */
      var sx = null, sy = null, cible = null;
      this.addEventListener('touchstart', function (e) {
        var l = e.target.closest('.pxd-line');
        if (!l || e.target.closest('.pxd-step')) return;
        cible = l; sx = e.touches[0].clientX; sy = e.touches[0].clientY;
      }, { passive: true });
      this.addEventListener('touchmove', function (e) {
        if (!cible) return;
        var dx = e.touches[0].clientX - sx;
        var dy = e.touches[0].clientY - sy;
        if (Math.abs(dy) > Math.abs(dx)) { cible = null; return; }
        if (dx < 0) cible.style.transform = 'translateX(' + Math.max(dx, -120) + 'px)';
      }, { passive: true });
      this.addEventListener('touchend', function (e) {
        if (!cible) return;
        var dx = e.changedTouches[0].clientX - sx;
        cible.style.transform = '';
        if (dx < -90) self._retirer(cible.dataset.key);
        cible = null;
      });
    }

    /* Le code promo Shopify ne se valide qu'au checkout : on l'enregistre
       (cookie /discount/) et on l'annonce honnêtement, sans faux calcul. */
    _appliquerPromo() {
      var input = this.querySelector('#PxDrawerPromo');
      var msg = this.querySelector('#PxDrawerPromoMsg');
      if (!input || !msg) return;
      var code = (input.value || '').trim();
      if (!code) { msg.textContent = this.strings.promoVide; return; }
      var self = this;
      fetch('/discount/' + encodeURIComponent(code))
        .then(function () { msg.textContent = self.strings.promoOk; })
        .catch(function () { msg.textContent = self.strings.erreur; });
    }

    /* L'icône panier du header ouvre le tiroir (href /cart en secours sans JS). */
    _interceptIconePanier() {
      var self = this;
      document.addEventListener('click', function (e) {
        var a = e.target.closest('a.cart-icon, a[href$="/cart"].nav-icon');
        if (!a) return;
        e.preventDefault();
        self._resync();
        self.open();
      });
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', PxCartDrawer);

  /* Helpers purs exposés pour la page panier (cart-page.js) — la grille de port n'existe
     qu'ICI (calculerPort), jamais dupliquée ailleurs en JS. */
  window.PxCart = { calculerPort: calculerPort, eur: eur, eurC: eurC, fenetreLivraison: fenetreLivraison };
})();
